import React, { useState } from 'react';
import { string, bool, func } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import {
  Form,
  PrimaryButton,
  SecondaryButton,
  FieldTextInput,
  FieldCheckbox,
  InlineTextButton,
  FieldLocationAutocompleteInput
} from '..';
import css from './HostDeliveryForm.module.css';

const identity = v => v;

const HostDeliveryFormComponent = props => {
  const [deliveryDistance, setDeliveryDistance] = useState(null);
  const [pickupDistance, setPickupDistance] = useState(null);

  const calculateDistance = async (location1, location2) => {
    const proxyUrl = `https://wetrentals.com/api/calculate-distance?location1=${encodeURIComponent(
      location1
    )}&location2=${encodeURIComponent(location2)}`;

    try {
      const response = await fetch(proxyUrl);
      const data = await response.json();

      if (data.distance !== undefined) {
        return data.distance;
      } else {
        throw new Error('Failed to calculate distance');
      }
    } catch (error) {
      console.error('Error calculating distance:', error);
      return null;
    }
  };

  const findClosestLocation = async (targetLocation, locations) => {
    let shortestDistance = Infinity;

    for (const loc of locations) {
      const address = loc.address || loc.location?.address;
      if (address) {
        const distance = await calculateDistance(targetLocation, address);
        if (distance < shortestDistance) {
          shortestDistance = distance;
        }
      }
    }

    return shortestDistance;
  };

  const handleCalculateDelivery = async (values, listing, perKm) => {
    const deliveryLocation = values.deliveryLocation?.selectedPlace?.address;
    const mainLocation = listing?.attributes?.publicData?.location?.address;
    const additionalLocations = listing?.attributes?.publicData?.additionalLocations || [];

    if (deliveryLocation && mainLocation) {
      const shortestDistance = await findClosestLocation(deliveryLocation, [
        { address: mainLocation },
        ...additionalLocations,
      ]);

      const deliveryAmount = shortestDistance * perKm * 2;
      setDeliveryDistance(deliveryAmount);
      props.onDeliveryAmountChange && props.onDeliveryAmountChange(deliveryAmount);
    }
  };

  const handleCalculatePickup = async (values, listing, perKm) => {
    const pickupLocation = values.pickupLocation?.selectedPlace?.address;
    const mainLocation = listing?.attributes?.publicData?.location?.address;
    const additionalLocations = listing?.attributes?.publicData?.additionalLocations || [];

    if (pickupLocation && mainLocation) {
      const shortestDistance = await findClosestLocation(pickupLocation, [
        { address: mainLocation },
        ...additionalLocations,
      ]);

      const pickupAmount = shortestDistance * perKm * 2;
      setPickupDistance(pickupAmount);
      props.onPickupAmountChange && props.onPickupAmountChange(pickupAmount);
    }
  };

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          values,
          invalid,
          handleSubmit,
          intl,
          hostDeliveryInProgress,
          hostDeliveryError,
          perKm,
          listing,
        } = fieldRenderProps;

        const deliveryLabel =
          deliveryDistance !== null
            ? `Set delivery charge for $${deliveryDistance.toFixed(2)} (adjust to discount)`
            : 'Calculate delivery cost first';
        const pickupLabel =
          pickupDistance !== null
            ? `Set pickup charge for $${pickupDistance.toFixed(2)} (adjust to discount)`
            : 'Calculate pickup cost first';

        const errorMessage = hostDeliveryError ? (
          <p className={css.error}>
            <FormattedMessage id="HostDelivery.Error" />
          </p>
        ) : null;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = hostDeliveryInProgress;
        const submitDisabled = invalid || submitInProgress;

        const termsLink = (
          <InlineTextButton
            rootClassName={css.helperLink}
            onClick={event => {
              event.preventDefault();
              window.open('https://wetrentals.com/terms-of-service#delivery', '_blank');
            }}
          >
            <FormattedMessage id="HostDelivery.Link" />
          </InlineTextButton>
        );

        const deliveryLocationLabel = (
          <FormattedMessage
            id="HostDeliveryForm.deliveryText"
            values={{ termsLink, perKm: perKm || 0 }}
            className={css.deliveryText}
          />
        );
        const deliveryLocationPlaceholder = intl.formatMessage({
          id: 'HostDeliveryForm.deliveryLocationPlaceholder',
        });
        const pickupLocationLabel = (
          <FormattedMessage
            id="HostDeliveryForm.pickupText"
            values={{ termsLink, perKm: perKm || 0 }}
            className={css.deliveryText}
          />
        );
        const pickupLocationPlaceholder = intl.formatMessage({
          id: 'HostDeliveryForm.pickupLocationPlaceholder',
        });

        return (
          <Form
            className={classes}
            onSubmit={handleSubmit}
            enforcePagePreloadFor="OrderDetailsPage"
          >
            {perKm && (
              <>
                <div className={css.section}>
                  <h3 className={css.sectionTitle}>Delivery</h3>
                  <FieldLocationAutocompleteInput
                    rootClassName={css.locationAddressWrapper}
                    className={css.locationAddress}
                    inputClassName={css.locationAutocompleteInput}
                    iconClassName={css.locationAutocompleteInputIcon}
                    predictionsClassName={css.predictionsRoot}
                    validClassName={css.validLocation}
                    name="deliveryLocation"
                    label={deliveryLocationLabel}
                    placeholder={deliveryLocationPlaceholder}
                    useDefaultPredictions={false}
                    format={identity}
                    valueFromForm={values.deliveryLocation}
                  />
                  <SecondaryButton
                    type="button"
                    onClick={() => handleCalculateDelivery(values, listing, perKm)}
                    className={css.calculateButton}
                  >
                    Calculate Delivery
                  </SecondaryButton>
                  
                  {deliveryDistance !== null && (
                    <div className={css.field}>
                      <FieldTextInput
                        className={css.field}
                        id={formId ? `${formId}.deliveryAmount` : 'deliveryAmount'}
                        name="deliveryAmount"
                        type="number"
                        label={deliveryLabel}
                        placeholder={deliveryDistance.toFixed(2)}
                        initialValue={deliveryDistance.toFixed(2)}
                        min="0"
                        max="1000"
                        step="0.01"
                      />
                    </div>
                  )}
                </div>
                
                <hr className={css.divider} />
                
                <div className={css.section}>
                  <h3 className={css.sectionTitle}>Pickup</h3>
                  <FieldLocationAutocompleteInput
                    rootClassName={css.locationAddressWrapper}
                    className={css.locationAddress}
                    inputClassName={css.locationAutocompleteInput}
                    iconClassName={css.locationAutocompleteInputIcon}
                    predictionsClassName={css.predictionsRoot}
                    validClassName={css.validLocation}
                    name="pickupLocation"
                    label={pickupLocationLabel}
                    placeholder={pickupLocationPlaceholder}
                    useDefaultPredictions={false}
                    format={identity}
                    valueFromForm={values.pickupLocation}
                  />
                  <SecondaryButton
                    type="button"
                    onClick={() => handleCalculatePickup(values, listing, perKm)}
                    className={css.calculateButton}
                  >
                    Calculate Pickup
                  </SecondaryButton>
                  
                  {pickupDistance !== null && (
                    <div className={css.field}>
                      <FieldTextInput
                        className={css.field}
                        id={formId ? `${formId}.pickupAmount` : 'pickupAmount'}
                        name="pickupAmount"
                        type="number"
                        label={pickupLabel}
                        placeholder={pickupDistance.toFixed(2)}
                        initialValue={pickupDistance.toFixed(2)}
                        min="0"
                        max="1000"
                        step="0.01"
                      />
                    </div>
                  )}
                </div>
              </>
            )}
            {errorMessage}
            {perKm && (
              <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                <FormattedMessage id="HostDeliveryForm.submitButtonText" />
              </PrimaryButton>
            )}
          </Form>
        );
      }}
    />
  );
};

HostDeliveryFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inProgress: false,
};

HostDeliveryFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  inProgress: bool,
  onDeliveryAmountChange: func,
  onPickupAmountChange: func,

  // from injectIntl
  intl: intlShape.isRequired,
};

const HostDeliveryForm = compose(injectIntl)(HostDeliveryFormComponent);

HostDeliveryForm.displayName = 'HostDeliveryForm';

export default HostDeliveryForm;